/**
 * FIREBASE CONTEXT v3.0 - SIMPLIFIED
 *
 * ANTES (v2.0): Sistema dual redundante
 *   onSnapshot → localStorage.setItem() → CustomEvent → AppContext listener
 *   Problema: localStorage era redundante porque Firebase ya tiene
 *   persistentLocalCache() configurado en firebase.js
 *   Problema: smartSync nunca funcionó (firestoreService no importado)
 *   Problema: CustomEvents son frágiles y difíciles de debuggear
 *
 * AHORA (v3.0): Pipeline directo
 *   onSnapshot → estado React en FirebaseContext
 *   StockContext/PlannerContext/ShoppingContext consumen via useFirebase()
 *
 * SIN localStorage manual. SIN CustomEvents. SIN smartSync.
 * Firebase persistentLocalCache maneja el offline automáticamente.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import { doc, collection, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { PRODUCT_CATALOG } from '../data/productCatalog';
import {
  getHousehold,
  subscribeToHousehold,
  subscribeToStock,
  subscribeToMealPlan,
  subscribeToShoppingLists,
  syncStockToFirestore,
  syncMealPlanToFirestore,
  syncShoppingListsToFirestore,
} from '../services/firestore';

const FirebaseContext = createContext();

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) throw new Error('useFirebase must be used within FirebaseProvider');
  return context;
};

export const FirebaseProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // ── Estado de sesión ──────────────────────────────────────────────────────
  const [userProfile, setUserProfile] = useState(null);
  const [household, setHousehold] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // ── Estado de datos (reemplaza localStorage + CustomEvents) ───────────────
  // Estos estados reciben los datos directamente desde onSnapshot.
  // StockContext, PlannerContext y ShoppingContext los consumen via useFirebase()
  // y los usan como fuente de verdad cuando Firebase está disponible.
  const [stockFromFirebase, setStockFromFirebase] = useState(null);
  const [mealPlanFromFirebase, setMealPlanFromFirebase] = useState(null);
  const [shoppingListsFromFirebase, setShoppingListsFromFirebase] = useState(null);
  const [productsFromFirebase, setProductsFromFirebase] = useState(null);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const loadHousehold = useCallback(async (id) => {
    try {
      const res = await getHousehold(id);
      if (res.success) setHousehold(res.household);
    } catch (e) {
      console.error('Error loading household:', e);
    }
  }, []);

  const createUserProfile = useCallback(async (userRef) => {
    try {
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName || 'Usuario',
        photoURL: user.photoURL || '',
        createdAt: serverTimestamp(),
        householdId: null,
      });
    } catch (e) {
      console.error('Error creando perfil:', e);
    }
  }, [user]);

  // ── syncToFirebase ─────────────────────────────────────────────────────────
  // Versión simplificada: sin smartSync, sin localStorage, solo Firestore directo.
  const syncToFirebase = useCallback(async (overrides = {}, options = {}) => {
    if (!household?.id || !syncEnabled) {
      return { success: false, error: 'No hay hogar o sync deshabilitado' };
    }

    setIsSyncing(true);

    try {
      const promises = [];

      if (overrides.stock) {
        promises.push(syncStockToFirestore(household.id, overrides.stock));
      }
      if (overrides.mealPlan) {
        promises.push(syncMealPlanToFirestore(household.id, overrides.mealPlan));
      }
      if (overrides.shoppingLists) {
        promises.push(syncShoppingListsToFirestore(household.id, overrides.shoppingLists));
      }

      // 👇 NUEVO: SELLO GLOBAL DE ACTUALIZACIÓN 👇
      // Si se modificó Stock o MealPlan, dejamos la firma del usuario en el documento del Hogar
      if (overrides.stock || overrides.mealPlan) {
        const householdRef = doc(db, 'households', household.id);
        promises.push(
          setDoc(householdRef, {
            lastUpdateDetails: {
              userId: user.uid,
              userName: user.displayName || user.email || 'Alguien',
              type: overrides.stock ? 'stock' : 'mealPlan',
              timestamp: serverTimestamp()
            }
          }, { merge: true }) // merge:true es clave para no borrar el resto de los datos del hogar
        );
      }
      // 👆 FIN DE NUEVO 👆

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      setLastSyncTime(new Date());
      setIsSyncing(false);
      return { success: true };
    } catch (error) {
      console.error('❌ Error en sync:', error);
      setIsSyncing(false);
      return { success: false, error: error.message };
    }
  }, [household?.id, syncEnabled, user]); // <-- Asegurate de que 'user' esté acá en las dependencias

  // ── 1. Perfil de usuario ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setUserProfile(null);
      return;
    }

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, async (docSnap) => {
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      } else {
        await createUserProfile(userRef);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, user, createUserProfile]);

  // ── 2. Household ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userProfile?.householdId) {
      setHousehold(null);
      setSyncEnabled(false);
      return;
    }
    loadHousehold(userProfile.householdId);
    setSyncEnabled(true);
  }, [userProfile?.householdId, loadHousehold]);

  useEffect(() => {
    if (!household?.id) return;
    const unsub = subscribeToHousehold(household.id, (data) => setHousehold(data));
    return () => unsub();
  }, [household?.id]);

  // ── 3. Stock ──────────────────────────────────────────────────────────────
  // onSnapshot → setStockFromFirebase (directo, sin localStorage ni CustomEvents)
  useEffect(() => {
    if (!household?.id || !syncEnabled) return;

    const unsub = subscribeToStock(household.id, (data) => {
      // null significa "todavía no cargó", [] significa "cargó y está vacío"
      setStockFromFirebase(data ?? []);
    });

    return () => unsub();
  }, [household?.id, syncEnabled]);

  // ── 4. Productos globales ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onSnapshot(
      collection(db, 'productBank'),
      (snapshot) => {
        const firestoreProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const finalProducts = firestoreProducts.length > 0
          ? firestoreProducts
          : (PRODUCT_CATALOG || []).map((p, i) => ({ ...p, id: p.id || `catalog_${i}`, fromCatalog: true }));
        setProductsFromFirebase(finalProducts);
      },
      (error) => {
        console.warn('⚠️ Error en productBank, usando catálogo local:', error);
        setProductsFromFirebase(
          (PRODUCT_CATALOG || []).map((p, i) => ({ ...p, id: p.id || `catalog_${i}`, fromCatalog: true }))
        );
      }
    );

    return () => unsubscribe();
  }, [isAuthenticated]);

  // ── 5. Meal Plan ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!household?.id || !syncEnabled) return;

    const unsub = subscribeToMealPlan(household.id, (data) => {
      setMealPlanFromFirebase(data ?? []);
    });

    return () => unsub();
  }, [household?.id, syncEnabled]);

  // ── 6. Shopping Lists ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!household?.id || !syncEnabled) return;

    const unsub = subscribeToShoppingLists(household.id, (data) => {
      setShoppingListsFromFirebase(data ?? []);
    });

    return () => unsub();
  }, [household?.id, syncEnabled]);

  // ── Value ─────────────────────────────────────────────────────────────────
  const value = useMemo(() => ({
    // Sesión
    household,
    userProfile,
    isSyncing,
    syncEnabled,
    lastSyncTime,
    setSyncEnabled,

    // Sync activo (escribir a Firestore)
    syncToFirebase,

    // Datos en tiempo real desde Firebase
    // Los contextos hijos (Stock, Planner, Shopping) observan estos valores
    // y actualizan su estado local cuando cambian.
    // null = todavía no llegó la primera respuesta de Firebase
    // []   = Firebase respondió con lista vacía
    stockFromFirebase,
    mealPlanFromFirebase,
    shoppingListsFromFirebase,
    productsFromFirebase,
  }), [
    household, userProfile, isSyncing, syncEnabled, lastSyncTime,
    syncToFirebase,
    stockFromFirebase, mealPlanFromFirebase, shoppingListsFromFirebase, productsFromFirebase,
  ]);

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};